import React, { useState } from "react";
import { useNavigate, Link  } from "react-router-dom";
import { adminLogin, sellerLogin, buyerLogin } from "../../api/auth.api";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";
import ForgotPassword from "./ForgotPassword";
import { validateLoginForm } from "../../utils/validationUtils";

// Vibration Animation Styles
const vibrationKeyframes = `
  @keyframes vibration {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
    20%, 40%, 60%, 80% { transform: translateX(5px); }
  }
  
  .form-vibrate {
    animation: vibration 0.5s ease-in-out;
  }
`;

const Login = ({ closeModal, openRegister }) => {
  const navigate = useNavigate();
  const { fetchUser } = useAuth();
  const [view, setView] = useState("select");
  const [values, setValues] = useState({ email: "", password: "", role: "SELLER" });
  const [showForgot, setShowForgot] = useState(false);
  const [errors, setErrors] = useState({});
  const [vibrating, setVibrating] = useState(false);

  const handleChange = (e) => {
    setValues({ ...values, [e.target.name]: e.target.value });
    // Clear error for this field when user starts typing
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: "" });
    }
  };

  const triggerVibration = () => {
    setVibrating(true);
    setTimeout(() => setVibrating(false), 500);
  };

  const handleRoleSelect = (role) => {
    setValues({ ...values, role });
    setView("form");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const { isValid, errors: validationErrors } = validateLoginForm(values.email, values.password, values.role);
    
    if (!isValid) {
      setErrors(validationErrors);
      triggerVibration();
      toast.error("Please fill in all fields correctly");
      return;
    }

    try {
      let res;
      if (values.role === "ADMIN") res = await adminLogin(values);
      else if (values.role === "SELLER") res = await sellerLogin(values);
      else if (values.role === "BUYER") res = await buyerLogin(values);

     if (res.data.isOk) {
        toast.success("Login successful");
        await fetchUser();
        closeModal();  
        if (values.role === "ADMIN") navigate("/admin/dashboard");
        else if (values.role === "SELLER") navigate("/seller/dashboard");
        else navigate("/");
      }
    } catch (error) {
      triggerVibration();
      toast.error(error.response?.data?.message || "Login failed");
    }
  };

  const containerStyle = { 
    maxWidth: "500px", 
    width: "90%",
    margin: "clamp(40px, 8vh, 80px) auto", 
    backgroundColor: "#fff", 
    padding: "clamp(20px, 5vw, 40px)", 
    borderRadius: "12px", 
    border: "1px solid #e5e7eb", 
    boxShadow: "0 10px 25px rgba(0,0,0,0.05)", 
    position: "relative" 
  };
  
  const roleCardStyle = { 
    display: "flex", 
    alignItems: "center", 
    gap: "clamp(10px, 2vw, 20px)", 
    padding: "clamp(12px, 2.5vw, 20px)", 
    border: "1px solid #e5e7eb", 
    borderRadius: "10px", 
    marginBottom: "15px", 
    cursor: "pointer" 
  };

  const inputStyle = { 
    backgroundColor: "#f9fafb", 
    border: "1px solid #e5e7eb", 
    borderRadius: "8px", 
    padding: "12px 16px", 
    width: "100%", 
    marginBottom: "20px", 
    outline: "none",
    fontSize: "clamp(14px, 1.2vw, 16px)"
  };

  return (
    <>
      <style>{vibrationKeyframes}</style>
      <div style={{...containerStyle}} className={vibrating ? "form-vibrate" : ""}>
        <span
          onClick={closeModal}
          style={{ position: "absolute", right: "20px", top: "20px", cursor: "pointer", fontSize: "20px" }}
        >
          ✕
        </span>
        {view === "select" ? (
          <>
            <h3 className="fw-bold mb-1" style={{ color: "#112347", fontSize: "clamp(20px, 3vw, 26px)" }}>Login to Aeternitas</h3>
            <p className="text-muted mb-4" style={{ fontSize: "clamp(13px, 1vw, 15px)" }}>Select your role to get started</p>
            
            <div style={roleCardStyle} onClick={() => handleRoleSelect("BUYER")}>
              <div style={{ padding: 'clamp(10px, 2vw, 15px)', background: '#f3f4f6', borderRadius: '8px' }}>👤</div>
              <div>
                <div className="fw-bold" style={{ color: "#112347", fontSize: "clamp(14px, 1.2vw, 16px)" }}>Buyer</div>
                <div className="text-muted" style={{ fontSize: "clamp(11px, 1vw, 13px)" }}>Browse & purchase industrial products</div>
              </div>
            </div>

            <div style={roleCardStyle} onClick={() => handleRoleSelect("SELLER")}>
              <div style={{ padding: 'clamp(10px, 2vw, 15px)', background: '#f3f4f6', borderRadius: '8px' }}>🏪</div>
              <div>
                <div className="fw-bold" style={{ color: "#112347", fontSize: "clamp(14px, 1.2vw, 16px)" }}>Seller</div>
                <div className="text-muted" style={{ fontSize: "clamp(11px, 1vw, 13px)" }}>Manage product listing & orders</div>
              </div>
            </div>

            <div style={roleCardStyle} onClick={() => handleRoleSelect("ADMIN")}>
              <div style={{ padding: 'clamp(10px, 2vw, 15px)', background: '#f3f4f6', borderRadius: '8px' }}>🛡️</div>
              <div>
                <div className="fw-bold" style={{ color: "#112347", fontSize: "clamp(14px, 1.2vw, 16px)" }}>Admin</div>
                <div className="text-muted" style={{ fontSize: "clamp(11px, 1vw, 13px)" }}>Manage marketplace operations</div>
              </div>
            </div>
          </>
        ) : (
          <>
            <button onClick={() => setView("select")} className="btn btn-link p-0 text-decoration-none fw-bold mb-4" style={{ color: "#112347", fontSize: "14px" }}>← Back</button>
            <h3 className="fw-bold mb-1" style={{ color: "#112347", fontSize: "clamp(20px, 3vw, 26px)" }}>Login as {values.role === "BUYER" ? "Buyer" : values.role === "SELLER" ? "Seller" : "Admin"}</h3>
            <p className="text-muted mb-4" style={{ fontSize: "clamp(13px, 1vw, 15px)" }}>Enter your credentials to continue</p>
            <form onSubmit={handleSubmit}>
              <label className="fw-bold small mb-2">Email or User ID</label>
              <input 
                type="email" 
                name="email" 
                placeholder="you@company.com" 
                style={{
                  ...inputStyle,
                  borderColor: errors.email ? "#ef4444" : "#e5e7eb",
                  backgroundColor: errors.email ? "#fef2f2" : "#f9fafb"
                }} 
                value={values.email} 
                onChange={handleChange} 
                required 
              />
              {errors.email && <p style={{ color: "#dc2626", fontSize: "12px", marginBottom: "15px" }}>⚠ {errors.email}</p>}

              <label className="fw-bold small mb-2">Password</label>
              <input 
                type="password" 
                name="password" 
                placeholder="•••••••" 
                style={{
                  ...inputStyle,
                  borderColor: errors.password ? "#ef4444" : "#e5e7eb",
                  backgroundColor: errors.password ? "#fef2f2" : "#f9fafb"
                }} 
                value={values.password} 
                onChange={handleChange} 
                required 
              />
              {errors.password && <p style={{ color: "#dc2626", fontSize: "12px", marginBottom: "15px" }}>⚠ {errors.password}</p>}

              {values.role !== "ADMIN" && (
              <div className="text-end mb-3">
                <span
                  onClick={() => setShowForgot(true)}
                  style={{ color: "#112347", fontSize: "13px", fontWeight: "600", cursor: "pointer" }}
                >
                  Forgot Password?
                </span>
              </div>
            )}
              <button type="submit" className="btn w-100 py-3 fw-bold text-white mt-2" style={{ backgroundColor: "#112347", fontSize: "clamp(14px, 1.2vw, 16px)" }}>Login</button>
            </form>
            {values.role !== "ADMIN" && (
              <div className="text-center mt-4 small text-muted">
                don't have an account? <span className="fw-bold text-dark" style={{ cursor: "pointer" }} onClick={() => openRegister(values.role)}>
                  {values.role === "SELLER" ? "Register" : "Sign up"}
                </span>
              </div>
            )}
          </>
        )}
        {showForgot && (
          <div
            onClick={() => setShowForgot(false)}
            style={{
              position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.5)",
              display: "flex", alignItems: "center", justifyContent: "center", zIndex: 99999
            }}
          >
            <div onClick={(e) => e.stopPropagation()} className="w-100 d-flex justify-content-center">
              <ForgotPassword closeModal={() => setShowForgot(false)} />
            </div>
          </div>
        )}
      </div>
    </>
  );
};
export default Login;